import { styled } from '@mui/material';
import { Extension } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Plugin } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useEffect, useRef, useState } from 'react';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import ResourceChip from 'shell/components/ResourceChip';
import RichTextToolbar from 'shell/components/RichTextToolbar';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { clipboardTextParser } from 'utils/helper';

export default forwardRef(
    (
        {
            autoFocus = false,
            defaultShowFormatting = true,
            defaultValue,
            disabled,
            disableSend,
            disableUpload,
            files,
            getNameById,
            isEditing = false,
            isReplyingTo,
            placeholder = 'Type a message',
            onChangeFile,
            onClickCancelEdit,
            onClickCancelReply,
            onSelect,
            onSend,
            ...rest
        },
        ref
    ) => {
        const editor = useEditor({
            autofocus: autoFocus ? 'end' : false,
            editorProps: { clipboardTextParser },
            extensions: [
                StarterKit.configure({
                    blockquote: false,
                    codeBlock: false,
                    heading: false,
                    horizontalRule: false,
                    strike: false,
                }),
                Placeholder.configure({
                    placeholder,
                    showOnlyWhenEditable: false,
                }),
                Underline,
                Link.configure({
                    HTMLAttributes: {
                        target: '_blank',
                    },
                }),
                Extension.create({
                    addProseMirrorPlugins() {
                        return [
                            new Plugin({
                                props: {
                                    handleKeyDown: (_, e) => {
                                        if (e.key === 'Backspace') {
                                            if (this.editor.isActive('bulletList')) {
                                                if (!this.editor.view.state.selection.$cursor) return false;
                                                const listItemText =
                                                    this.editor.view.state.selection.$cursor.parent.textContent;
                                                if (!listItemText) {
                                                    this.editor.commands.toggleBulletList();
                                                    return true;
                                                }
                                            }

                                            if (this.editor.isActive('orderedList')) {
                                                if (!this.editor.view.state.selection.$cursor) return false;
                                                const listItemText =
                                                    this.editor.view.state.selection.$cursor.parent.textContent;
                                                if (!listItemText) {
                                                    this.editor.commands.toggleOrderedList();
                                                    return true;
                                                }
                                            }
                                        }

                                        if (e.key === 'Enter') {
                                            if (e.shiftKey) {
                                                if (
                                                    this.editor.isActive('bulletList') ||
                                                    this.editor.isActive('orderedList')
                                                ) {
                                                    this.editor.chain().focus().splitListItem('listItem').run();
                                                    return true;
                                                }
                                            } else {
                                                sendButtonRef.current.click();
                                                return true;
                                            }
                                        }
                                    },
                                },
                            }),
                        ];
                    },
                }),
            ],
            content: defaultValue,
        });

        const [showToolbar, setShowToolbar] = useState(defaultShowFormatting);
        const fileInputRef = useRef(null);
        const sendButtonRef = useRef(null);

        const handleToggleToolbar = () => {
            setShowToolbar((current) => !current);
            editor.commands.focus();
        };

        const handleSend = () => {
            if (disableSend) return;
            if (editor.isEmpty && !files?.length) return;
            onSend(editor.isEmpty ? '' : editor.getHTML().replaceAll('<br>', '</p><p>'), isReplyingTo);
            editor.commands.clearContent();
        };

        useEffect(() => {
            if (!editor) return;
            editor.setEditable(!disabled);
        }, [editor, disabled]);

        return (
            <CustomStack disabled={disabled} spacing={2} {...rest}>
                {isReplyingTo && (
                    <ReplyStack>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <ReplyAuthorStack direction={'row'}>
                                <Text noWrap variant={'p3'}>
                                    Replying to{' '}
                                    <Text component={'span'} noWrap variant={'p3'} weight={'semibold'}>
                                        {getNameById(isReplyingTo.owner_account_id)}
                                    </Text>
                                </Text>
                            </ReplyAuthorStack>
                            <CustomIconButton
                                aria-label={'close reply'}
                                kind={'exit'}
                                size={'small'}
                                variant={'square'}
                                onClick={onClickCancelReply}
                            />
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <CustomIcon kind={'reply'} />
                            <Text sx={{ flex: 1 }} color={'light'} noWrap rich variant={'p3'}>
                                {isReplyingTo.text}
                            </Text>
                        </Stack>
                        <CustomDivider />
                    </ReplyStack>
                )}
                {showToolbar && <RichTextToolbar disabled={disabled} editor={editor} />}
                <CustomBox disabled={disabled}>
                    <EditorContent data-cy={'feedback-input'} ref={ref} editor={editor} />
                </CustomBox>
                {files?.length ? (
                    <FileStack direction={'row'} spacing={3}>
                        {files.map((file) => (
                            <CustomResourceChip
                                key={file.id}
                                options={[{ key: 'delete', value: 'Delete' }]}
                                resource={file}
                                size={'medium'}
                                type={'file'}
                                onSelect={(option) => onSelect(option, file)}
                            />
                        ))}
                    </FileStack>
                ) : null}
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={1}>
                        <IconButton
                            aria-label={'attach file'}
                            disabled={disabled || disableUpload || files?.length > 0}
                            kind={'attach'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => fileInputRef.current.click()}
                        />
                        <CustomInput ref={fileInputRef} type={'file'} onChange={onChangeFile} />
                        <IconButton
                            aria-label={'toggle text formatting toolbar'}
                            disabled={disabled}
                            kind={showToolbar ? 'formatSelected' : 'formatDeselected'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleToggleToolbar}
                        />
                    </Stack>
                    {isEditing ? (
                        <Stack direction={'row'} spacing={2}>
                            <Button color={'neutral'} size={'small'} variant={'tertiary'} onClick={onClickCancelEdit}>
                                Cancel
                            </Button>
                            <Button
                                data-cy={'feedback-edit-save-button'}
                                ref={sendButtonRef}
                                size={'small'}
                                onClick={handleSend}
                            >
                                Save
                            </Button>
                        </Stack>
                    ) : (
                        <IconButton
                            data-cy={'send-button'}
                            aria-label={'send'}
                            ref={sendButtonRef}
                            disabled={disabled || disableSend}
                            kind={'send'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleSend}
                        />
                    )}
                </Stack>
            </CustomStack>
        );
    }
);

const CustomStack = styled(Stack)(({ disabled, theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
        borderRadius: '16px',
        width: '100%',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.shades[0],
        '&:focus-within': {
            borderColor: theme.palette.ensightful.primary[700],
            outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
            outlineOffset: '2px',
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[700],
        },
        ...(disabled && {
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            '&:focus-within': {
                outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
                outlineOffset: '2px',
            },
            '&:hover': {
                borderColor: theme.palette.ensightful.neutral[500],
            },
        }),
    };
});

const ReplyStack = styled(Stack)(({ theme }) => {
    return {
        marginTop: theme.spacing(-1),
    };
});

const ReplyAuthorStack = styled(Stack)(() => {
    return {
        minWidth: 0,
    };
});

const CustomIconButton = styled(IconButton)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '1rem',
    };
});

const CustomDivider = styled(Divider)(({ theme }) => {
    return {
        margin: theme.spacing(1, -2, 0),
    };
});

const CustomBox = styled(Box)(({ disabled, theme }) => {
    return {
        maxHeight: '6.75rem',
        padding: theme.spacing(0, 2),
        overflowY: 'auto',
        '& p': {
            margin: 0,
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            lineHeight: '21px',
        },
        '& .ProseMirror-focused': {
            outline: 'none',
        },
        '& .ProseMirror p.is-editor-empty:first-child::before': {
            color: theme.palette.ensightful.neutral[500],
            content: 'attr(data-placeholder)',
            float: 'left',
            height: '0',
            pointerEvents: 'none',
            ...(disabled && {
                color: theme.palette.ensightful.neutral[400],
            }),
        },
    };
});

const FileStack = styled(Stack)(() => {
    return {
        flexWrap: 'wrap',
        maxHeight: '10rem',
        overflowY: 'auto',
    };
});

const CustomResourceChip = styled(ResourceChip)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomInput = styled('input')(() => {
    return {
        display: 'none',
    };
});
